import { useEffect, useState } from "react";
import api from "../services/api";

export type BackendStatus = "online" | "verificando" | "offline" | "reconectado";

const INTERVALO_OK_MS = 10_000;   // 10s entre pings quando tudo ok
const INTERVALO_FAIL_MS = 3_000;  // 3s quando offline (tenta reconectar rápido)
const TIMEOUT_PING_MS = 3_000;

/**
 * Monitora a saúde do backend chamando /api/ping em intervalos.
 * - Estado inicial: "verificando" (sem ping ainda).
 * - Sucesso: "online".
 * - Falha (timeout, network error, 5xx): "offline".
 * - Quando volta: "reconectado" por 3s e depois "online".
 *
 * Também escuta eventos do SSE para reagir mais rápido — se o stream cair, marca offline imediatamente.
 */
export function useBackendStatus() {
  const [status, setStatus] = useState<BackendStatus>("verificando");
  const [ultimaTentativa, setUltimaTentativa] = useState<number>(0);

  useEffect(() => {
    let cancelado = false;
    let timer: number | null = null;

    async function pingar() {
      try {
        await api.get("/ping", { timeout: TIMEOUT_PING_MS });
        if (cancelado) return;
        setStatus((atual) => {
          if (atual === "offline") {
            setTimeout(() => !cancelado && setStatus("online"), 3000);
            return "reconectado";
          }
          return "online";
        });
      } catch (_e) {
        if (cancelado) return;
        setStatus("offline");
      } finally {
        if (cancelado) return;
        setUltimaTentativa(Date.now());
        const proxima = status === "offline" ? INTERVALO_FAIL_MS : INTERVALO_OK_MS;
        timer = window.setTimeout(pingar, proxima);
      }
    }

    pingar();

    // Reage a eventos rápidos — qualquer request que falhar com network error
    // dispara este evento e validamos com ping imediato
    function onRedeFalhou() {
      // Um blip do SSE/rede nao e "servidor fora": confirma com o ping (a verdade)
      // antes de mostrar o banner. So o pingar() decide o status.
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(pingar, 200);
    }
    window.addEventListener("growsoft:rede-falhou", onRedeFalhou);
    window.addEventListener("online", () => { setStatus("verificando"); pingar(); });
    window.addEventListener("offline", () => setStatus("offline"));

    return () => {
      cancelado = true;
      if (timer) window.clearTimeout(timer);
      window.removeEventListener("growsoft:rede-falhou", onRedeFalhou);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { status, ultimaTentativa };
}
